/**
 * /customer/rewards — rewards catalog across joined brands (Phase 2c).
 *
 * Mirrors the mobile rewards.tsx surface verbatim in copy & flow:
 *   - Header: REWARDS / "Trade stamps for treats"
 *   - Brand chip row: "All brands" + one chip per joined card
 *   - Vertical grid of reward cards with locked / available state
 *
 * No redemption happens here — every reward card is a `<Link>` to
 * /customer/reward/:id where the actual flow lives.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Gift, Lock, CheckCircle2, Star } from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

export default function CustomerRewardsPage() {
  const token = useAuthStore((s) => s.token);
  const [activeBrand, setActiveBrand] = useState('all');

  const rewardsQ = useQuery({
    queryKey: qk.customerMyRewards(),
    queryFn: () => api.customerMyRewards(token),
    enabled: !!token,
  });
  const cardsQ = useQuery({
    queryKey: qk.customerCards(),
    queryFn: () => api.customerCards(token),
    enabled: !!token,
  });

  // Brand chips are the joined cards' brands — mirrors mobile rewards.tsx.
  const brandChips = useMemo(
    () =>
      (cardsQ.data ?? [])
        .map((c) => c.brand)
        .filter((b) => b && b.id),
    [cardsQ.data],
  );

  const visible = useMemo(() => {
    const list = rewardsQ.data ?? [];
    return activeBrand === 'all'
      ? list
      : list.filter((r) => r.brand_id === activeBrand);
  }, [rewardsQ.data, activeBrand]);

  const hasCards = (cardsQ.data ?? []).length > 0;
  const isLoading = rewardsQ.isLoading || cardsQ.isLoading;

  return (
    <div className="space-y-6" data-testid="customer-rewards-screen">
      <div>
        <div className="text-xs uppercase tracking-widest text-gold font-mono mb-2">
          Rewards
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold">
          Trade stamps for treats
        </h1>
        <p className="text-sm text-white/55 mt-1.5">
          Unlock rewards across every brand you collect stamps with.
        </p>
      </div>

      {/* Brand chip row — only when there's at least one joined card */}
      {brandChips.length > 0 ? (
        <div className="flex flex-wrap gap-2" data-testid="customer-rewards-chip-row">
          <Chip
            active={activeBrand === 'all'}
            onClick={() => setActiveBrand('all')}
            testID="customer-rewards-chip-all"
          >
            All brands
          </Chip>
          {brandChips.map((b) => (
            <Chip
              key={b.id}
              active={activeBrand === b.id}
              onClick={() => setActiveBrand(b.id)}
              testID={`customer-rewards-chip-${b.slug ?? b.id}`}
            >
              {b.logo_url ? (
                <img
                  src={resolveImageUrl(b.logo_url)}
                  alt=""
                  className="w-4 h-4 rounded-md"
                />
              ) : null}
              {b.name}
            </Chip>
          ))}
        </div>
      ) : null}

      {/* Loading skeleton, empty states, or grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-56 rounded-3xl bg-white/[0.03] border border-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : !hasCards ? (
        <EmptyNoCards />
      ) : visible.length === 0 ? (
        <div
          className="rounded-3xl border border-white/8 bg-[#15151B] px-5 py-12 text-center"
          data-testid="customer-rewards-empty-state"
        >
          <Gift size={28} className="mx-auto text-gold/55 mb-3" />
          <div className="text-sm font-medium">No rewards yet</div>
          <div className="text-xs text-white/45 mt-1">
            This brand has no active rewards for you right now.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {visible.map((r) => (
            <RewardCard key={r.id} reward={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, testID, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testID}
      className={
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ' +
        (active
          ? 'bg-gold/15 border-gold/45 text-gold'
          : 'bg-[#15151B] border-white/10 text-white/65 hover:text-white hover:border-white/20')
      }
    >
      {children}
    </button>
  );
}

function RewardCard({ reward }) {
  const required = reward.progress?.required ?? reward.stamps_required;
  const current = reward.progress?.current ?? 0;
  const remaining = Math.max(0, required - current);
  const isAvailable = reward.status === 'available';
  const pct = Math.min(100, Math.round((current / Math.max(1, required)) * 100));
  const tint = reward.brand_primary_color || '#C9A84C';

  return (
    <Link
      to={`/customer/reward/${reward.id}`}
      data-testid={`customer-reward-card-${reward.id}`}
      className="group rounded-3xl bg-[#15151B] border border-white/8 hover:border-gold/35 hover:shadow-glow-gold-sm transition-all overflow-hidden flex flex-col"
    >
      {/* Image hero — gold tint fallback if no image */}
      <div
        className="h-32 relative shrink-0"
        style={
          reward.image_url
            ? {
                backgroundImage: `url(${resolveImageUrl(reward.image_url)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : { background: `linear-gradient(135deg, ${tint}55 0%, #0A0A0A 100%)` }
        }
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent" />
        {/* Brand pill (top-left) */}
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/55 backdrop-blur border border-white/10">
          {reward.brand_logo_url ? (
            <img
              src={resolveImageUrl(reward.brand_logo_url)}
              alt=""
              className="w-4 h-4 rounded-md"
            />
          ) : null}
          <span className="text-[11px] font-medium text-white/85 truncate max-w-[140px]">
            {reward.brand_name}
          </span>
        </div>
        {/* Status pill (top-right) */}
        <div
          className={
            'absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border ' +
            (isAvailable
              ? 'bg-gold text-black border-gold'
              : 'bg-black/55 text-white/65 border-white/15 backdrop-blur')
          }
          data-testid={
            isAvailable
              ? `customer-reward-card-${reward.id}-available`
              : `customer-reward-card-${reward.id}-locked`
          }
        >
          {isAvailable ? (
            <>
              <CheckCircle2 size={12} />
              Available
            </>
          ) : (
            <>
              <Lock size={11} />
              {remaining} more
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-4 flex-1 flex flex-col">
        <div
          className="font-display text-base font-semibold leading-tight"
          style={{ color: tint }}
        >
          {reward.title}
        </div>
        {reward.description ? (
          <div className="text-xs text-white/55 mt-1.5 line-clamp-2">
            {reward.description}
          </div>
        ) : null}
        <div className="mt-3 flex items-center gap-3">
          <div
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold text-[11px] font-semibold shrink-0"
          >
            <Star size={11} />
            {reward.stamps_required} stamps
          </div>
          <div className="flex-1 h-1.5 rounded-full bg-white/8 overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${pct}%`,
                backgroundColor: tint,
              }}
            />
          </div>
        </div>
      </div>
    </Link>
  );
}

function EmptyNoCards() {
  return (
    <div
      className="rounded-3xl border border-white/8 bg-[#15151B] px-6 py-12 text-center"
      data-testid="customer-rewards-empty-no-cards"
    >
      <div className="w-16 h-16 rounded-full bg-gold/10 border border-gold/25 grid place-items-center mx-auto mb-4">
        <Gift size={26} className="text-gold" />
      </div>
      <div className="text-base font-semibold">No joined brands</div>
      <div className="text-xs text-white/55 mt-1 max-w-xs mx-auto">
        Join a brand to start earning stamps and unlocking rewards.
      </div>
      <Link
        to="/customer/brands"
        data-testid="customer-rewards-empty-explore"
        className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 rounded-xl bg-gold/15 border border-gold/45 text-gold text-sm font-medium hover:bg-gold/20 transition-colors"
      >
        Explore brands
      </Link>
    </div>
  );
}
